import { API_BASE } from '../../apiBase';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

function ItemPopup({ item, setSelectedItem, cart, setCart, updateItem, role, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(item.name || '');
  const [editLocation, setEditLocation] = useState(item.location || '');
  const [editQuantity, setEditQuantity] = useState((item.quantity ?? 0).toString());
  const [editPrice, setEditPrice] = useState((item.price ?? 0).toString());
  const [editImage, setEditImage] = useState(item.image || '');
  const [editUnitLabel, setEditUnitLabel] = useState(item.unitLabel || 'Price Per Item');
  const [editAliases, setEditAliases] = useState(item.aliases ? item.aliases.join(', ') : '');
  const [editDescription, setEditDescription] = useState(item.description || '');
  const [editReturnable, setEditReturnable] = useState(!!item.returnable);
  const [editOwned, setEditOwned] = useState((item.ownedQuantity ?? item.quantity ?? 0).toString());
  const [showDescription, setShowDescription] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  // Tracks whether the CURRENT preview image is safely stored in Dropbox
  // (either the item was already mirrored, or a device upload just landed).
  const [imgSafe, setImgSafe] = useState(!!item.imageMirrored);
  const fileInputRef = useRef(null);

  const [action, setAction] = useState(item.quantity > 0 ? 'withdraw' : 'deposit');
  const [quantity, setQuantity] = useState('1');

  const placeholderImage = 'https://placehold.co/100x100?text=No+Image';

  // Sync fields when item changes
  useEffect(() => {
    setEditName(item.name || '');
    setEditLocation(item.location || '');
    setEditQuantity((item.quantity ?? 0).toString());
    setEditPrice((item.price ?? 0).toString());
    setEditImage(item.image || '');
    setEditUnitLabel(item.unitLabel || 'Price Per Item');
    setEditAliases(item.aliases ? item.aliases.join(', ') : '');
    setEditDescription(item.description || '');
    setEditReturnable(!!item.returnable);
    setEditOwned((item.ownedQuantity ?? item.quantity ?? 0).toString());
    setShowDescription(false);
    setImgSafe(!!item.imageMirrored);
  }, [item]);

  const parseLocation = (loc) => {
    const parts = String(loc || '').split('-');
    const rack = parts[0];
    const shelf = parts[1] ? parts[1][0] : '';
    const bin = parts[1] && parts[1].length > 1 ? parts[1].slice(1) : '';
    let readable = `Location: Rack ${rack}`;
    if (shelf) readable += `, Shelf ${shelf}`;
    if (bin) readable += `, Bin ${bin}`;
    return readable;
  };

  const handleTransaction = () => {
    const finalQuantity = Math.max(1, parseInt(quantity) || 1);
    if (finalQuantity > 0) {
      setCart([
        ...cart,
        {
          itemId: item._id,
          name: item.name,
          location: item.location,
          action,
          quantity: finalQuantity,
          price: item.price,
        },
      ]);
      setSelectedItem(null);
    }
  };

  const handleQuantityBlur = () => {
    setQuantity(Math.max(1, parseInt(quantity) || 1).toString());
  };

  // Upload a photo from the phone/computer. The server converts it to JPG and
  // stores it in Dropbox, so it's immediately link-rot-proof.
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API_BASE}/items/upload-image`, fd, {
        headers: { Authorization: localStorage.getItem('token') },
      });
      setEditImage(res.data.url);
      setImgSafe(true);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert(err.response?.data?.error || 'Could not upload the image.');
    } finally {
      setUploadingImg(false);
    }
  };

  const handleEditToggle = async () => {
    if (isEditing) {
      const updatedItem = {
        name: editName,
        description: editDescription,
        location: editLocation,
        quantity: parseInt(editQuantity) || 0,
        price: parseFloat(editPrice) || 0,
        image: editImage,
        unitLabel: editUnitLabel,
        aliases: editAliases ? editAliases.split(',').map(a => a.trim()).filter(a => a) : [],
        returnable: editReturnable,
        // Owned can't be below what's on the shelf (server enforces it too).
        ownedQuantity: editReturnable
          ? Math.max(parseInt(editOwned) || 0, parseInt(editQuantity) || 0)
          : null,
      };

      try {
        const res = await axios.put(
          `${API_BASE}/items/${item._id}`,
          updatedItem,
          { headers: { Authorization: localStorage.getItem('token') } }
        );
        updateItem(res.data);
        setIsEditing(false);
      } catch (err) {
        console.error('Update failed:', err);
        alert('Save failed — check console');
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      // Reset to original values
      setEditName(item.name || '');
      setEditLocation(item.location || '');
      setEditQuantity((item.quantity ?? 0).toString());
      setEditPrice((item.price ?? 0).toString());
      setEditImage(item.image || '');
      setEditUnitLabel(item.unitLabel || 'Price Per Item');
      setEditAliases(item.aliases ? item.aliases.join(', ') : '');
      setEditDescription(item.description || '');
      setEditReturnable(!!item.returnable);
      setEditOwned((item.ownedQuantity ?? item.quantity ?? 0).toString());
      setImgSafe(!!item.imageMirrored);
      setIsEditing(false);
    } else {
      setSelectedItem(null);
    }
  };

  // Shared input style — big touch targets for the phone.
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: '1rem',
    borderRadius: '10px', border: '1px solid #ccc', backgroundColor: '#fff',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#666',
    marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.03em',
  };

  return (
    <div>
      {isEditing ? (
        /* === EDIT MODE — stacked, full-width, phone-first === */
        <div>
          {/* Image preview + status + upload */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ position: 'relative' }}>
              <img
                src={editImage || placeholderImage}
                alt="Preview"
                style={{
                  width: '128px', height: '128px', objectFit: 'contain',
                  border: '2px solid #ddd', borderRadius: '12px', backgroundColor: '#fff',
                }}
              />
              {/* Little "safely downloaded" badge: this image now lives in your
                  Dropbox — the original URL can die and nothing breaks. */}
              {imgSafe && editImage && (
                <span
                  title="Image saved to your Dropbox — safe even if the original link breaks"
                  style={{
                    position: 'absolute', bottom: '-6px', right: '-6px',
                    width: '26px', height: '26px', borderRadius: '50%',
                    background: '#28a745', color: 'white', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '14px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)', border: '2px solid white',
                  }}
                >⬇</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploadingImg}
              style={{
                marginTop: '10px', padding: '10px 18px', borderRadius: '10px',
                border: '1px solid #ccc', background: '#f8f9fa', cursor: 'pointer',
                fontSize: '0.9rem', fontWeight: 600,
              }}
            >
              {uploadingImg ? 'Uploading…' : '📷 Upload photo'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { handleFileUpload(e.target.files && e.target.files[0]); e.target.value = ''; }}
            />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); handleEditToggle(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
          >
            <div>
              <label style={labelStyle}>Item Name</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} style={inputStyle} placeholder="Item Name" />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Location</label>
                <input value={editLocation} onChange={(e) => setEditLocation(e.target.value)} style={inputStyle} placeholder="e.g., 1-A2" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Available</label>
                <input type="number" inputMode="numeric" value={editQuantity} onChange={(e) => setEditQuantity(e.target.value)} style={inputStyle} min="0" />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={editReturnable}
                onChange={(e) => {
                  setEditReturnable(e.target.checked);
                  // First time marking it returnable: assume all are on the shelf.
                  if (e.target.checked && !item.returnable) setEditOwned(editQuantity || '0');
                }}
                style={{ width: '20px', height: '20px' }}
              />
              Returnable (tool — gets checked out and brought back)
            </label>
            {editReturnable && (
              <div>
                <label style={labelStyle}>Total Owned</label>
                <input type="number" inputMode="numeric" value={editOwned} onChange={(e) => setEditOwned(e.target.value)} style={inputStyle} min="0" />
                <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#888' }}>
                  Shows as "{parseInt(editQuantity) || 0} of {Math.max(parseInt(editOwned) || 0, parseInt(editQuantity) || 0)}" — {Math.max(0, (parseInt(editOwned) || 0) - (parseInt(editQuantity) || 0))} checked out.
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Price ($)</label>
                <input type="number" inputMode="decimal" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} style={inputStyle} min="0" step="0.01" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Unit</label>
                <select value={editUnitLabel} onChange={(e) => setEditUnitLabel(e.target.value)} style={inputStyle}>
                  <option value="Price Per Item">Price Per Item</option>
                  <option value="Price Per Box">Price Per Box</option>
                  <option value="Price per foot">Price per foot</option>
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Slang Names</label>
              <input value={editAliases} onChange={(e) => setEditAliases(e.target.value)} style={inputStyle} placeholder="Comma-separated (barcodes land here too)" />
            </div>
            <div>
              <label style={labelStyle}>Description (optional)</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Extra details — specs, what it's for, compatible parts…"
              />
            </div>
            <div>
              <label style={labelStyle}>Image URL</label>
              <input
                value={editImage}
                onChange={(e) => { setEditImage(e.target.value); setImgSafe(false); }}
                style={inputStyle}
                placeholder="Paste an image URL — we'll save a safe copy automatically"
              />
              {!imgSafe && editImage && (
                <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#888' }}>
                  A copy will be saved to your Dropbox automatically after you hit Save.
                </p>
              )}
              {/* The URL originally pasted, kept for reference after mirroring
                  re-pointed `image` at the safe Dropbox copy. */}
              {item.originalImageUrl && (
                <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#888', wordBreak: 'break-all' }}>
                  Original source:{' '}
                  <a href={item.originalImageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                    {item.originalImageUrl}
                  </a>
                </p>
              )}
            </div>
            <button type="submit" style={{ display: 'none' }}></button>
          </form>
        </div>
      ) : (
        /* === VIEW MODE === */
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ width: '100px', minWidth: '100px', height: '100px', flexShrink: 0, overflow: 'hidden' }}>
            <img
              src={item.image || placeholderImage}
              alt={item.name}
              style={{
                display: 'block',
                width: '100px',
                height: '100px',
                objectFit: 'contain',
              }}
            />
          </div>
          <div style={{ flex: 1, paddingLeft: '10px', minWidth: '220px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {item.name}
              {/* Tiny toggle — most items don't need a description, so it stays
                  out of the way until asked for. */}
              {item.description && (
                <button
                  type="button"
                  onClick={() => setShowDescription((s) => !s)}
                  title={showDescription ? 'Hide description' : 'Show description'}
                  style={{
                    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                    border: '1.5px solid ' + (showDescription ? '#007bff' : '#bbb'),
                    background: showDescription ? '#007bff' : 'white',
                    color: showDescription ? 'white' : '#888',
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, padding: 0,
                  }}
                >
                  i
                </button>
              )}
            </h3>
            {showDescription && item.description && (
              <p style={{
                margin: '8px 0', padding: '10px 12px', background: '#f8f9fa',
                borderRadius: '8px', fontSize: '0.88rem', color: '#444',
                whiteSpace: 'pre-wrap', border: '1px solid #eee',
              }}>
                {item.description}
              </p>
            )}
            <p style={{ margin: '5px 0' }}>{parseLocation(item.location)}</p>
            <p style={{ margin: '5px 0' }}>
              Available: {item.returnable && item.ownedQuantity != null ? `${item.quantity} of ${item.ownedQuantity}` : item.quantity}
              {item.returnable && item.ownedQuantity > item.quantity && (
                <span style={{ color: '#dc3545', fontSize: '0.85rem' }}> · {item.ownedQuantity - item.quantity} checked out</span>
              )}
            </p>
            <p style={{ margin: '5px 0' }}>
              {item.unitLabel || 'Price Per Item'}: ${Number(item.price ?? 0).toFixed(2)}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
              <select value={action} onChange={(e) => setAction(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
                {item.quantity > 0 && <option value="withdraw">Withdraw</option>}
                <option value="deposit">{item.returnable ? 'Return' : 'Deposit'}</option>
              </select>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max={action === 'withdraw' ? item.quantity : 9999}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onBlur={handleQuantityBlur}
                style={{ width: '70px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
              />
              <button
                onClick={handleTransaction}
                style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#007bff', color: 'white', fontWeight: 600, cursor: 'pointer' }}
              >
                Enter Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button
          onClick={handleCancel}
          style={{ flex: 1, padding: '13px', borderRadius: '10px', border: '1px solid #ccc', background: '#f1f3f5', fontWeight: 600, cursor: 'pointer' }}
        >
          Cancel
        </button>
        {['admin', 'superadmin'].includes(role) && (
          <button
            onClick={handleEditToggle}
            disabled={uploadingImg}
            style={{ flex: 1, padding: '13px', borderRadius: '10px', border: 'none', background: isEditing ? '#28a745' : '#007bff', color: 'white', fontWeight: 700, cursor: 'pointer' }}
          >
            {isEditing ? 'Save' : 'Edit'}
          </button>
        )}
      </div>

      {!isEditing && (
        <p style={{ marginTop: '10px', fontSize: '0.75rem', color: '#888' }}>
          Slang Names:{' '}
          {Array.isArray(item.aliases) && item.aliases.length > 0
            ? item.aliases.join(', ')
            : 'None'}
        </p>
      )}

      {/* Super admin: delete this item outright (confirm handled by the parent). */}
      {!isEditing && role === 'superadmin' && onDelete && (
        <button
          onClick={() => onDelete(item)}
          style={{
            marginTop: '12px', width: '100%', padding: '12px', borderRadius: '10px',
            border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626',
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          🗑 Delete Item
        </button>
      )}
    </div>
  );
}

export default ItemPopup;
